// Path: ./src/app/components/FilterBar/index.tsx
import * as React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import TuneIcon from "@mui/icons-material/Tune";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTheme } from "@mui/material/styles";
import SelectMui from "../SelectMui";
import { messages } from "../../../locales/messages";
import { useTranslation } from "react-i18next";
import { MultipleSelect } from "../MultiSelectDropDown";
import { AutocompleteOption } from "../CustomAutocomplete";

export interface FilterSelectOption {
  name: string;
  selectOptions: AutocompleteOption<string>[];
  multiple?: boolean;
  onSelectMultiple?: (value: string[]) => void;
  onSelectSingleItem?: (value: string) => void;
}

interface Props {
  filterListItems?: FilterSelectOption[];
}
const compactFieldWidth = 140;

const controlSx = {
  minHeight: 34,
  fontSize: 13,
  "& .MuiSelect-select": {
    padding: "6px 10px",
    minHeight: 18,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(17, 24, 39, 0.1)",
    transition: "border-color 120ms ease, box-shadow 120ms ease",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ff8038",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ff8038",
    boxShadow: "0 0 0 2px rgba(255, 128, 56, 0.15)",
  },
};

export default function FilterBar(props: Props) {
  const { filterListItems } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [open, setOpen] = React.useState(true);

  const toggle = () => setOpen((prev) => !prev);

  const renderFilters = (layout: "row" | "column") => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          layout === "row" ? "repeat(auto-fit, minmax(140px, 1fr))" : "1fr",
        gap: layout === "row" ? 0.75 : 1,
        columnGap: layout === "row" ? 1 : 1,
        alignItems: "start",
      }}
    >
      {filterListItems?.map((value, index) => (
        <Stack
          key={`${value.name}-${index}`}
          spacing={0.25}
          sx={{
            backgroundColor: "rgba(17, 24, 39, 0.02)",
            border: "1px solid rgba(17, 24, 39, 0.06)",
            borderRadius: 1,
            px: 0.75,
            py: 0.5,
            minWidth: compactFieldWidth,
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#ff8038",
                boxShadow: "0 0 0 3px rgba(255,128,56,0.12)",
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.1,
                textTransform: "uppercase",
              }}
            >
              {value.name}
            </Typography>
          </Stack>
          {value.multiple ? (
            <MultipleSelect
              selectOptions={value.selectOptions}
              wrapperStyle={`
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                width: 100%;
              `}
              FormControlStyle={{
                width: "100%",
                margin: 0,
              }}
              onSelect={value.onSelectMultiple}
              dataTestId={value.name}
            ></MultipleSelect>
          ) : (
            <SelectMui
              selectOptions={value.selectOptions}
              onSelect={value.onSelectSingleItem}
              dataTestId={value.name}
              wrapperStyle={`min-width:${compactFieldWidth}px;width:100%;`}
              FormControlStyle={{
                width: "100%",
                margin: 0,
              }}
              StyledTextFieldStyle={controlSx}
            ></SelectMui>
          )}
        </Stack>
      ))}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      data-testid="drawerappbar-test-id"
      sx={{
        p: { xs: 0.9, sm: 1.05 },
        borderRadius: 1.5,
        border: "1px solid rgba(255,128,56,0.18)",
        background: "linear-gradient(135deg, #ffffff 0%, #fff7ef 100%)",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: isDesktop ? 0.25 : 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              backgroundColor: "#fff2e8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(255,128,56,0.25)",
            }}
          >
            <TuneIcon sx={{ fontSize: 16, color: "#ff8038" }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t(messages.Filters())}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: "none", sm: "block" }, lineHeight: 1.2 }}
            >
              {t(messages.SEARCH_FOR_IMAGE())}
            </Typography>
          </Box>
        </Stack>

        <IconButton size="small" onClick={toggle} aria-label="toggle filters">
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Stack>

      <Collapse in={open} timeout="auto">
        <Box sx={{ mt: isDesktop ? 0.5 : 1 }}>
          {renderFilters(isDesktop ? "row" : "column")}
        </Box>
      </Collapse>
    </Paper>
  );
}
